import React, { useEffect, useState, useRef, useMemo, ChangeEvent, FC } from 'react';
import classNames from 'classnames';

import { useDebouncedEffect } from 'src/common/hooks';
import JourneyAutocomplete from 'src/common/form/JourneyAutocomplete';
import { Journey } from 'src/common/interfaces/journey.interface';
import { SearchStrongIcon } from 'src/monet/icons';
import { apiGetAllJourneysQuickSearch } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';

interface JourneySearchInputProps {
  autoFocus?: boolean;
  optionClassName?: string;
  optionsContainerClassName?: string;
  onSelectJourney: (journey: Journey | null) => void;
}

export const JourneySearchInput: FC<JourneySearchInputProps> = ({
  onSelectJourney,
  autoFocus,
  optionClassName = 'focus:bg-neue-journey-accent-10 active:bg-neue-journey-accent-10',
  optionsContainerClassName = 'bg-[#F55722]',
}) => {
  const [journeyOptionsOpen, setJourneyOptionsOpen] = useState(false);
  const [journeySearchQuery, setJourneySearchQuery] = useState('');
  const [journeysPage, setJourneysPage] = useState(1);
  const [journeyOptions, setJourneyOptions] = useState<Journey[]>([]);
  const [journeysLoading, setJourneysLoading] = useState(false);
  const hasMoreJourneysRef = useRef(false);

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);

  const visibleJourneyOptions = useMemo(() => {
    return journeyOptionsOpen ? journeyOptions : [];
  }, [journeyOptionsOpen, journeyOptions]);

  useEffect(() => {
    setJourneysPage(1);
    if (journeySearchQuery === '') {
      onSelectJourney(null);
    }
  }, [journeySearchQuery]);

  useDebouncedEffect(
    async () => {
      try {
        setJourneysLoading(true);
        const journeyResponse = await apiGetAllJourneysQuickSearch(currentOrganization.id, {
          searchStr: journeySearchQuery,
          page: journeysPage,
          per_page: 8,
        });
        setJourneysLoading(false);

        const { page, pages, journeys }: { page: number; pages: number; journeys: Journey[] } = journeyResponse;
        if (page === 1) {
          setJourneyOptions(journeys);
        } else {
          setJourneyOptions((journeyOptions) => [...journeyOptions, ...journeys]);
        }
        hasMoreJourneysRef.current = page < pages;
      } catch (e) {
        setJourneysLoading(false);
        //console.error(e);
      }
    },
    250,
    [journeySearchQuery, journeysPage]
  );

  return (
    <JourneyAutocomplete
      autoFocus={autoFocus}
      placeholder='Search for Journey'
      id='search-for-journey'
      filterSelectedOptions={false}
      onInputChange={(_: ChangeEvent<{}>, newInputValue: string) => {
        setJourneySearchQuery(newInputValue);
      }}
      filterOptions={(x: Journey) => x}
      onChange={(_: ChangeEvent<{}>, option: Journey) => {
        if (option) {
          onSelectJourney(option);
        }
      }}
      onScrollReachBottom={() => {
        if (hasMoreJourneysRef.current) {
          setJourneysPage((page) => page + 1);
        }
      }}
      openOnFocus={true}
      options={visibleJourneyOptions}
      isNeue={true}
      loading={journeysLoading}
      textInputProps={{
        inputClasses: 'w-full pl-9',
        onBlur: (e: any) => {
          setJourneyOptionsOpen(false);
        },
        onFocus: () => {
          setJourneyOptionsOpen(true);
        },
        inlineAddon: (
          <div
            className={classNames('absolute left-2 top-2.5 transition-colors', {
              'text-neue-journey-fg': journeyOptionsOpen,
              'text-neue-journey-fg-50': !journeyOptionsOpen,
            })}
          >
            <SearchStrongIcon className='w-5 h-5' />
          </div>
        ),
      }}
      getOptionLabel={(option: Journey) => option.name}
      renderOption={(option: Journey) => (
        <div className={classNames('py-[9px] px-2 text-neue-journey-fg rounded', optionClassName)}>
          <div className='text-bedrock-p truncate'>{option.name}</div>
        </div>
      )}
      showEmptyOption={false}
      optionsContainerClassName={optionsContainerClassName}
    />
  );
};
